/*
 * Free gifts for the offer tiers.
 *
 * Keeps the cart's gift lines equal to what the offer lines say they should
 * be. The wanted set is read off the cart itself: every offer line carries an
 * `_offer_tier_map` property holding the offer's whole tier table, written by
 * the tier selector. Nothing here needs configuring, and nothing here needs the
 * selector to be on the page, which is what makes "remove the mask on /cart and
 * the gifts go with it" work.
 *
 * The table rather than the chosen tier, because a line property is written
 * once at add to cart and Shopify keeps it through quantity changes. Storing
 * the tier that was picked stored an answer, and the answer stopped describing
 * the cart the moment the shopper edited the quantity: a jar raised from one to
 * two earned nothing, and a jar dropped from two to one kept a gift it no
 * longer qualified for, which the loop below then dutifully restored every time
 * it was deleted. Storing the table stores the rule instead, and the quantity
 * is read live from the cart.
 *
 * Deliberately separate from free-gift.js rather than folded into it. That
 * file reconciles the Mystery Gift on every cart in the store; a mistake in
 * here would then reach every customer. Isolated, the blast radius is carts
 * that contain an offer line, and the two converge on their own: a write here
 * makes free-gift.js re-check its threshold, and its writes make this
 * re-check, and both are idempotent so it settles after one pass each.
 *
 * Gift lines are marked with `_offer_gift` so this only ever touches its own,
 * and with `_free_gift` so the cart row and badge treatment the theme already
 * has for the Mystery Gift applies unchanged: no stepper, no product link, a
 * gratis badge.
 */
(function () {
  var OFFER_PROPERTY = '_offer_gifts';
  var TIER_MAP_PROPERTY = '_offer_tier_map';
  var TIER_TITLES_PROPERTY = '_offer_tier_titles';
  var TIER_PROPERTY = '_offer_tier';
  var GIFT_MARKER = '_offer_gift';
  /* Written onto every gift line so the cart can say where it came from: the
     tier that earned it, for the green pill, and the handle of the line that
     earned it, for the subtitle. Both are left off when two different products
     earned the same gift, which is Sam's rule of 12 September: the serum is
     generic precisely because it is earned twice. */
  var GIFT_TIER_PROPERTY = '_offer_gift_tier';
  var GIFT_SOURCE_PROPERTY = '_offer_gift_source';
  var SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  var busy = false;
  var again = false;
  var timer = null;
  var pending = false;
  var failures = 0;
  /* KK-1891. The cart as the last pass saw it, which is what decides whether
     a shopper's add may carry its gift along (see giftsWithAdd). */
  var knownCart = null;

  /* KK-1525. Set on the init of every request this file sends, so the fetch
     wrapper at the bottom can tell our writes from everyone else's by looking
     at the request itself. It replaces a counter that said "something of ours
     is in flight", which answers a different question: while our serum add
     was in flight, free-gift.js added the cotton pads, the counter called that
     write ours, and the pass that would have noticed the serum was gone never
     ran. */
  var OWN_REQUEST = 'offerGiftsRequest';

  function isTrue(value) {
    return value === 'true' || value === true;
  }

  function ownFetch(url, init) {
    init[OWN_REQUEST] = true;
    return fetch(url, init);
  }

  /* A write Shopify refused is a pass that did not do what it set out to do.
     Failing the pass makes the queue run it again instead of drawing the
     drawer over a cart that is missing a line. */
  function checkWrite(response) {
    if (!response.ok) throw new Error('cart write failed: ' + response.status);
    return response;
  }

  function cartJson() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (response) {
      if (!response.ok) throw new Error('cart fetch failed');
      return response.json();
    });
  }

  /* Duplicates are kept. A tier that names the same product in two gift slots
     earns two of it, and the whole point of the change of 12 September is that
     the engine counts rather than lists: de-duplicating here is what made two
     offers hand over one serum, and it is what made the mask's third tier need
     two different free products to show two rows. */
  function variantIds(raw) {
    var ids = [];

    String(raw || '').split(',').forEach(function (part) {
      var id = parseInt(part, 10);
      if (id) ids.push(id);
    });

    return ids;
  }

  /* Rows read "quantity:variant,variant" and are separated by ";". A tier with
     no gifts is written as "1:", so an empty gift list stays distinguishable
     from a line carrying no table. Sorted here rather than trusted from the
     markup, because the answer depends on the order. */
  function tierRows(raw) {
    var rows = [];

    String(raw).split(';').forEach(function (row) {
      var split = row.indexOf(':');
      if (split === -1) return;

      var threshold = parseInt(row.slice(0, split), 10);
      if (!threshold) return;

      rows.push({ threshold: threshold, gifts: variantIds(row.slice(split + 1)) });
    });

    return rows.sort(function (a, b) {
      return b.threshold - a.threshold;
    });
  }

  /* What this line earns at the quantity it is now, which is the highest tier
     its quantity reaches. A quantity below every tier earns nothing. */
  function giftsForLine(item) {
    var properties = item.properties || {};
    var map = properties[TIER_MAP_PROPERTY];

    if (map) {
      var rows = tierRows(map);

      for (var i = 0; i < rows.length; i += 1) {
        if (item.quantity >= rows[i].threshold) return rows[i].gifts;
      }

      return [];
    }

    /* Lines added before this shipped carry only the tier they were bought at.
       Keep honouring it so a cart that is already open does not lose its gift
       mid-session; those lines keep the old frozen behaviour until re-added. */
    return variantIds(properties[OFFER_PROPERTY]);
  }

  /* KK-1527. Rows read "quantity:title" and are separated by ";", the same
     shape as the tier map, sorted the same way and for the same reason. */
  function titleRows(raw) {
    var rows = [];

    String(raw).split(';').forEach(function (row) {
      var split = row.indexOf(':');
      if (split === -1) return;

      var threshold = parseInt(row.slice(0, split), 10);
      if (!threshold) return;

      rows.push({ threshold: threshold, title: row.slice(split + 1) });
    });

    return rows.sort(function (a, b) {
      return b.threshold - a.threshold;
    });
  }

  /* The tier this line is at now, by its quantity, which is what its gifts'
     pill should name. `_offer_tier` holds the tier that was picked on the
     page, and a line property is written once: a paid line raised in the
     cart from two to three kept tier 2's words on its free line while the
     count on that line already said tier 3. Lines added before the table
     shipped carry no table and keep the picked tier, as before. */
  function tierForLine(item) {
    var properties = item.properties || {};
    var table = properties[TIER_TITLES_PROPERTY];

    if (table) {
      var rows = titleRows(table);

      for (var i = 0; i < rows.length; i += 1) {
        if (item.quantity >= rows[i].threshold) return rows[i].title;
      }

      return '';
    }

    return properties[TIER_PROPERTY] || '';
  }

  /* How many of each gift the cart has earned, and from which lines.

     The count is what Sam asked for on 12 September: two offers that each earn
     a serum earn two serums, they show as one line at quantity two, and taking
     one of the two qualifying products out drops the line to one by arithmetic
     rather than by a rule about serums.

     The attribution is what the cart row needs. A gift earned by exactly one
     product carries that product's tier title on its pill and its handle for
     the subtitle. A gift earned by two different products carries neither, so
     it falls back to the generic gratis product label. */
  function wantedGifts(cart) {
    var wanted = {};

    (cart.items || []).forEach(function (item) {
      /* A gift line never earns gifts of its own. It carries no tier map, so
         this is already true, and stating it stops a future map on a gift line
         from looping the cart. */
      if (item.properties && isTrue(item.properties[GIFT_MARKER])) return;

      var tier = tierForLine(item);

      giftsForLine(item).forEach(function (id) {
        var key = String(id);
        var entry = wanted[key];

        if (!entry) {
          entry = wanted[key] = { id: id, count: 0, tiers: [], handles: [] };
        }

        entry.count += 1;
        if (entry.tiers.indexOf(tier) === -1) entry.tiers.push(tier);
        if (entry.handles.indexOf(item.handle) === -1) entry.handles.push(item.handle);
      });
    });

    return wanted;
  }

  /* The properties a gift line should carry, given what earned it. The two
     markers are unchanged: `_free_gift` is what the theme's own cart row and
     badge treatment key on, `_offer_gift` is what keeps this file off the
     Mystery Gift. */
  function giftProperties(entry) {
    var properties = { _free_gift: 'true', _offer_gift: 'true' };
    var handles = entry.handles.filter(Boolean);
    var tiers = entry.tiers.filter(Boolean);

    if (handles.length === 1) {
      properties[GIFT_SOURCE_PROPERTY] = handles[0];
      if (tiers.length === 1) properties[GIFT_TIER_PROPERTY] = tiers[0];
    }

    return properties;
  }

  /* Whether the line already carries those properties. A line whose attribution
     changed, because a second offer joined the cart or left it, is corrected in
     place rather than removed and re-added: re-adding would lose its position in
     the cart and flash an empty row. */
  function propertiesMatch(line, properties) {
    var current = line.properties || {};
    var keys = [GIFT_TIER_PROPERTY, GIFT_SOURCE_PROPERTY];

    for (var i = 0; i < keys.length; i += 1) {
      var want = properties[keys[i]] || '';
      var have = current[keys[i]] || '';
      if (want !== have) return false;
    }

    return true;
  }

  function giftLines(cart) {
    return (cart.items || []).filter(function (item) {
      return item.properties && isTrue(item.properties[GIFT_MARKER]);
    });
  }

  /* KK-1545. Every gift a pass owes goes out in one request: /cart/add takes
     an items array, so the two gifts of a tier cost one round trip rather
     than two. The trade, named on the ticket before it was built: Shopify
     refuses the whole request when any one line in it cannot be added, so a
     sold out gift now takes the pass's other new gifts down with it, where
     the one request per gift form lost only the gifts queued behind it.
     Either way checkWrite fails the pass and the next one retries.

     The .js endpoint, measured rather than assumed: /cart/add without it and
     without an Accept header answers the items form with a redirect to the
     cart page, 500 KB of HTML nobody reads and one round trip more. */
  function addGifts(items) {
    return ownFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: items,
        sections: SECTIONS,
        sections_url: window.location.pathname
      })
    }).then(checkWrite).then(readSections);
  }

  /* KK-1548. The .js endpoint, like the add above: without it Shopify answered
     this JSON body with a redirect to /cart and the whole cart page, 88 KB a
     write, for an answer nothing here reads.

     `properties` is optional, and omitted rather than sent empty when only the
     quantity moves: `/cart/change` replaces a line's whole property set, so
     sending a partial one would strip the two markers off the line and the
     theme would stop treating it as a gift. */
  function setLineQuantity(lineKey, quantity, properties) {
    var body = {
      id: lineKey,
      quantity: quantity,
      sections: SECTIONS,
      sections_url: window.location.pathname
    };

    if (properties) body.properties = properties;

    return ownFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(checkWrite).then(readSections);
  }

  /* KK-1891. Every gift line whose quantity alone is wrong, removals
     included, goes out in one /cart/update.js keyed by line key rather than
     one /cart/change.js each. Quantities only: a line whose label is also
     wrong keeps its own change above, because update.js cannot set line
     properties. Keyed by line key, not variant id, for the reason KK-1695
     found: a variant can sit on two lines. If Shopify refuses the merged
     write, the same lines go one at a time, the path this file ran before. */
  function setLineQuantities(updates) {
    return ownFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        updates: updates,
        sections: SECTIONS,
        sections_url: window.location.pathname
      })
    }).then(function (response) {
      if (response.ok) return readSections(response);

      return Object.keys(updates).reduce(function (chain, lineKey) {
        return chain.then(function () {
          return setLineQuantity(lineKey, updates[lineKey]);
        });
      }, Promise.resolve(null));
    });
  }

  /* KK-1891. A write to /cart/change or /cart/update answers with the whole
     cart, the same object /cart.js returns. The fetch wrapper at the bottom
     keeps the last such answer, and a pass that starts before any other cart
     write has gone out uses it instead of reading /cart.js again, one round
     trip fewer after every quantity change in the drawer and after every pass
     of ours that ends on a change. Shared through window so free-gift.js
     reads it too. Two seconds at most, so a write that never passed through
     the wrapper cannot keep a stale cart in use for long. */
  var SNAPSHOT_MAX_AGE = 2000;

  function snapshotCart() {
    var snapshot = window.__elanora_cart_snapshot;
    if (!snapshot || snapshot.seq !== window.__elanora_cart_writes) return null;
    if (Date.now() - snapshot.at > SNAPSHOT_MAX_AGE) return null;
    return snapshot.cart;
  }

  /* KK-1552. Every write above asks Shopify for the drawer sections and gets
     them back in the same answer, and until now the pass threw that answer
     away and fetched the same sections again in refreshDrawer, one round trip
     more on every pass that writes, measured at 150 to 750 ms on live. The
     last write of a pass describes the cart as the pass leaves it, so its
     sections are what the drawer should show. An answer without sections,
     or one that is not JSON, hands back null and the pass falls back to the
     separate fetch, which is the path free-gift.js has run since KK-1548. */
  function readSections(response) {
    return response.json().then(function (data) {
      return data && data.sections ? data.sections : null;
    }, function () {
      return null;
    });
  }

  function renderDrawer(sections) {
    if (!sections) return false;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') return false;
    try {
      drawer.renderContents({ sections: sections });
      return true;
    } catch (error) {
      return false;
    }
  }

  /* KK-1515. Nik, 12 September, on the gap before the free product appears:
     "can we at least show a loading state so customers understand that the
     offer is being added?"

     The gap is this file's own second pass, four round trips deep and
     deliberately sequential, and it starts after the shopper's own add has
     already returned and drawn the drawer. So the drawer looks settled and
     then changes, which is the thing he is reporting.

     The drawer already owns the answer: `assets/cart-drawer.js` listens for
     these two events on the document, reference counts them and keeps its
     overlay up for a minimum of a second, and the theme raises the same pair
     for its own quantity changes. One start and one stop per reconcile pass is
     the contract, and the stop runs on failure too, so a lost request can never
     leave the drawer looking busy. */
  function loadingStart() {
    if (pending) return;
    pending = true;
    document.dispatchEvent(new CustomEvent('cart:drawer-loading-start'));
  }

  function loadingStop() {
    if (!pending) return;
    pending = false;
    document.dispatchEvent(new CustomEvent('cart:drawer-loading-stop'));
  }

  function refreshDrawer() {
    return ownFetch(window.location.pathname + '?sections=' + SECTIONS.join(','), {
      credentials: 'same-origin'
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        renderDrawer(data);
      })
      .catch(function () {});
  }

  function reconcile() {
    if (busy) {
      again = true;
      return Promise.resolve();
    }

    busy = true;

    /* The sections carried by the last write of this pass, null until a write
       answers with them. See readSections. */
    var lastSections = null;

    var snapshot = snapshotCart();

    return (snapshot ? Promise.resolve(snapshot) : cartJson())
      .then(function (cart) {
        knownCart = cart;
        var wanted = wantedGifts(cart);
        var wantedKeys = Object.keys(wanted);
        var present = giftLines(cart);

        /* Nothing to do for every cart in the store that has no offer line. */
        if (!wantedKeys.length && !present.length) return false;

        var jobs = [];
        var seen = {};
        var quantities = {};
        var quantityCount = 0;

        present.forEach(function (line) {
          var key = String(line.variant_id);
          var entry = wanted[key];

          /* Not earned any more, or a second line of a gift already kept: the
             count lives on one line and the rest go. */
          if (!entry || seen[key]) {
            quantities[line.key] = 0;
            quantityCount += 1;
            return;
          }

          seen[key] = true;

          /* A gift line carries as many units as the cart has earned, and the
             attribution of what earned them. Either can be wrong on its own:
             a second offer joining the cart changes the count and takes the
             tier label off, and a second offer leaving puts both back. */
          var properties = giftProperties(entry);
          var quantityWrong = line.quantity !== entry.count;
          var attributionWrong = !propertiesMatch(line, properties);

          if (attributionWrong) {
            jobs.push(function () {
              return setLineQuantity(line.key, entry.count, properties);
            });
          } else if (quantityWrong) {
            quantities[line.key] = entry.count;
            quantityCount += 1;
          }
        });

        /* The quantity only writes first, as one request (KK-1891). */
        if (quantityCount) {
          jobs.unshift(function () { return setLineQuantities(quantities); });
        }

        var adds = [];

        wantedKeys.forEach(function (key) {
          if (seen[key]) return;
          var entry = wanted[key];
          adds.push({ id: entry.id, quantity: entry.count, properties: giftProperties(entry) });
        });

        /* One write for every gift the cart is missing, after the changes
           above, which keep their own order. */
        if (adds.length) {
          jobs.push(function () { return addGifts(adds); });
        }

        if (!jobs.length) return false;

        /* Only once there is work. Every cart in the store without an offer
           line returns above this line and raises nothing. */
        loadingStart();

        /* Sequential, and the queue holds thunks rather than promises:
           an array of already started requests would run concurrently, and
           Shopify drops overlapping writes to the same cart. */
        return jobs.reduce(function (chain, job) {
          return chain.then(job).then(function (sections) {
            lastSections = sections || null;
          });
        }, Promise.resolve()).then(function () {
          failures = 0;
          return true;
        });
      })
      .then(function (changed) {
        if (!changed) return null;
        if (renderDrawer(lastSections)) return null;
        return refreshDrawer();
      })
      .catch(function () {
        /* A refused write or a dropped connection. One more pass, and a
           third, then stop: a gift Shopify will not sell is not worth a loop. */
        failures += 1;
        if (failures <= 2) again = true;
      })
      .then(function () {
        loadingStop();
        busy = false;
        if (again) {
          again = false;
          return schedule(60);
        }
        return null;
      });
  }

  function schedule(delay) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () {
      timer = null;
      reconcile();
    }, typeof delay === 'number' ? delay : 120);
  }

  /* Our own drawer render raises these mid pass; a pass that is already
     running has nothing to learn from them. Anything that actually changed
     the cart went through the fetch wrapper below and is scheduled there. */
  ['cart:refresh', 'cart:updated', 'cart:open', 'cart:drawer-rendered'].forEach(function (name) {
    document.addEventListener(name, function () {
      if (pending) return;
      schedule(120);
    });
  });

  /* Kept on purpose (KK-1891, measured 29 September): the add's answer comes
     back slowly because it renders the drawer, while Shopify has already
     written the line, so this pass 500 ms after the click usually finds the
     new line and sends the gift before the shopper's add has even answered.
     Taking it out made an offer add about 0.75 s slower. */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (form && form.action && /\/cart\/add/.test(form.action)) schedule(500);
  });

  /* free-gift.js already wraps fetch; wrapping again is what catches the
     theme's own add to cart, which never fires a cart event of its own.

     KK-1525. This wrapper is the last one installed, so every cart write on
     the page passes through it first, and it lets one through at a time.
     Measured on 13 September with a fresh cart at two jars of eye patches:
     our serum add, free-gift.js's cotton pads add and our free jar add all
     went out inside one second, all four writes came back 200, and the cart
     held three of the four lines. Shopify keeps the last write to land and
     the write it overlapped is gone, so which gift went missing was a matter
     of timing: the serum on Sam's machine, the cotton pads here. Writes that
     never overlap cannot lose each other, and the wait costs nothing, since
     each writer already waits for its own response. */
  var originalFetch = window.fetch.bind(window);
  var writes = Promise.resolve();

  function noop() {}

  /* KK-1891. The gift travels inside the shopper's own add, so the pass
     after it finds the cart already right and writes nothing: the separate
     gift add, 0.8 to 1 s on live, is gone. Only for the first offer into a
     cart, the common case, where the answer is certain: the cart this file
     last saw holds no offer line and no gift line, so the gift's count and
     its label depend on this add alone, exactly as wantedGifts would work
     them out. In every other case the add goes out untouched and the pass
     does what it did before.

     The label names the product by its default language handle, because the
     pass reads /cart.js without a locale and compares against that; on /en/
     the address bar holds the English handle, the x-default alternate link
     the Dutch one. */
  function defaultHandle() {
    var link = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
    var href = (link && link.getAttribute('href')) || window.location.pathname;
    var match = /\/products\/([^/?#]+)/.exec(href);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function holdsOffers(cart) {
    return (cart.items || []).some(function (item) {
      var properties = item.properties || {};
      return isTrue(properties[GIFT_MARKER]) || !!properties[TIER_MAP_PROPERTY] || !!properties[OFFER_PROPERTY];
    });
  }

  function giftsWithAdd(body) {
    if (!knownCart || holdsOffers(knownCart)) return null;
    if (typeof FormData === 'undefined' || !(body instanceof FormData)) return null;

    var map = body.get('properties[' + TIER_MAP_PROPERTY + ']');
    var id = body.get('id');
    var quantity = parseInt(body.get('quantity') || '1', 10);
    if (!map || !id || !quantity) return null;

    var bundled = false;
    body.forEach(function (value, key) {
      if (key.indexOf('items[') === 0) bundled = true;
    });
    if (bundled) return null;

    var lineProperties = {};
    lineProperties[TIER_MAP_PROPERTY] = map;
    lineProperties[TIER_TITLES_PROPERTY] = body.get('properties[' + TIER_TITLES_PROPERTY + ']') || '';
    lineProperties[TIER_PROPERTY] = body.get('properties[' + TIER_PROPERTY + ']') || '';
    if (!lineProperties[TIER_TITLES_PROPERTY]) delete lineProperties[TIER_TITLES_PROPERTY];

    var line = { quantity: quantity, properties: lineProperties };
    var gifts = giftsForLine(line);
    var handle = defaultHandle();
    if (!gifts.length || !handle) return null;

    var tier = tierForLine(line);
    var counts = {};
    gifts.forEach(function (giftId) {
      counts[giftId] = (counts[giftId] || 0) + 1;
    });

    var merged = new FormData();
    body.forEach(function (value, key) {
      if (key === 'id' || key === 'quantity' || key === 'selling_plan') {
        merged.append('items[0][' + key + ']', value);
      } else if (key.indexOf('properties[') === 0) {
        merged.append('items[0][properties]' + key.slice('properties'.length), value);
      } else {
        merged.append(key, value);
      }
    });

    Object.keys(counts).forEach(function (giftId, index) {
      var properties = giftProperties({ id: giftId, count: counts[giftId], tiers: [tier], handles: [handle] });
      var prefix = 'items[' + (index + 1) + ']';
      merged.append(prefix + '[id]', giftId);
      merged.append(prefix + '[quantity]', String(counts[giftId]));
      Object.keys(properties).forEach(function (key) {
        merged.append(prefix + '[properties][' + key + ']', properties[key]);
      });
    });

    return merged;
  }

  window.__elanora_cart_writes = window.__elanora_cart_writes || 0;

  /* KK-1891. Keep the whole cart a change or update answered with, tagged
     with the number of the write it came from. snapshotCart above trusts it
     only while no later write has started. */
  function keepSnapshot(url, seq, response) {
    if (!/\/cart\/(change|update)/.test(url) || !response || !response.ok) return;

    response.clone().json().then(function (data) {
      if (!data || !Array.isArray(data.items) || typeof data.item_count !== 'number') return;
      if (seq !== window.__elanora_cart_writes) return;
      window.__elanora_cart_snapshot = { cart: data, seq: seq, at: Date.now() };
    }, noop);
  }

  window.fetch = function offerGiftsFetchWrapper(input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartWrite = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);

    if (!isCartWrite) return originalFetch(input, init);

    window.__elanora_cart_writes += 1;
    var seq = window.__elanora_cart_writes;

    /* KK-1891. The shopper's add with its gift inside, when giftsWithAdd can
       vouch for it. If Shopify refuses the combined add, for example because
       the gift cannot be sold in this market, the shopper's own add goes out
       again exactly as the form sent it, and the pass handles the gift the
       old way: the gift may fail, the shopper's product must not. */
    var withGift = null;
    if (/\/cart\/add(\.js)?(\?|$)/.test(url) && init && init.body && !init[OWN_REQUEST]) {
      try { withGift = giftsWithAdd(init.body); } catch (error) { withGift = null; }
    }

    /* Any write makes the cart the last pass saw out of date, so a second
       quick add waits for the next pass instead of carrying a second gift. */
    knownCart = null;

    var result = writes.then(function () {
      if (!withGift) return originalFetch(input, init);

      var combined = {};
      Object.keys(init).forEach(function (key) { combined[key] = init[key]; });
      combined.body = withGift;

      return originalFetch(input, combined).then(function (response) {
        return response.ok ? response : originalFetch(input, init);
      }, function () {
        return originalFetch(input, init);
      });
    });

    result.then(function (response) {
      keepSnapshot(url, seq, response);
    }, noop);

    /* The next write waits for this one, or for ten seconds, whichever comes
       first, so a request the browser never settles cannot hold every later
       cart write of the page hostage. */
    writes = Promise.race([
      result,
      new Promise(function (resolve) { setTimeout(resolve, 10000); })
    ]).then(noop, noop);

    /* KK-1891. An add starts the pass as soon as it lands; it used to wait
       400 ms more for nothing, the writes being one at a time already. */
    if (!(init && init[OWN_REQUEST])) {
      result.then(function () {
        schedule(/\/cart\/add/.test(url) ? 0 : 120);
      }, noop);
    }

    return result;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      schedule(0);
    });
  } else {
    schedule(0);
  }
})();
